import { FeatureIds } from "@redbelly-tokeniser/mystake-common-sdk";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../auth/AuthProvider";
import { ActivePlan, navbarDataMap, parentPageTitleMap } from "../../constants/constants";
import { Badge } from "../../designSystem";
import { companyService } from "../../util";
import { canShowActOnBehalf } from "../companies/register/fatcaCrs/canShowActOnBehalf";
import { useCompanyContext } from "../companies/useCompanyContext";
import { isApproverReadOnly } from "./useApproverReadOnly";

// Write-action tabs hidden from approvers on Company Information nested screens; read-only tabs stay.
const APPROVER_HIDDEN_TABS_BY_SECTION: Record<string, string[]> = {
  Officeholders: ["appoint", "remove"],
  Options: ["create-pools", "grant"],
  "Options grant": ["vest", "exercise"],
  Notes: ["create-notes", "issue-notes", "notes-certificate"],
  Shares: ["share-certificate", "share-eligibility", "share-claims"],
};

const EXPIRING_KYC_REGISTER_TAB = {
  label: "Expiring KYC",
  value: "expiring-kyc-register",
};

const PENDING_AUTHORITY_REQUESTS_TAB = {
  label: "Pending requests for authority",
  value: "pending-authority-requests",
};

const REGISTER_PAGE_PATHS = [
  "/company/:companyId/shares-register",
  "/company/:companyId/options-register",
  "/company/:companyId/notes-register",
  "/company/:companyId/expiring-kyc-register",
  "/company/:companyId/pending-authority-requests",
];

export const isRegistersPagePath = (pathname: string): boolean =>
  REGISTER_PAGE_PATHS.some((path) => matchPath(pathname, { path, exact: true }));

const FUND_REGISTRY_TITLES: Record<string, string> = {
  "Company Information": "Fund Information",
  Shares: "Units",
  "Share price history": "Unit price history",
};

type NavbarItem = {
  label: string;
  value: string;
};

const formatTabCount = (count: number): string => (count > 99 ? "99+" : String(count));

export const resolvePageTitle = (
  pathname: string,
  isAsicConnectionActivated: boolean,
  isFundRegistry = false,
  /** The dealing settings page keeps its redemption route but covers both sides once this is on. */
  subscriptionsOffered = false
): string => {
  const pathKeys = Object.keys(parentPageTitleMap);
  for (const pathKey of pathKeys) {
    const foundPath = matchPath(pathname, { path: pathKey, exact: true });
    if (foundPath) {
      const title = parentPageTitleMap[foundPath.path];
      if (!isAsicConnectionActivated && title === "Issue Shares") {
        return "Allocate Shares";
      }
      if (isFundRegistry && FUND_REGISTRY_TITLES[title]) {
        return FUND_REGISTRY_TITLES[title];
      }
      if (title === "Redemption settings" && subscriptionsOffered) {
        return "Dealing and liquidity";
      }
      return title;
    }
  }
  return "";
};

/** Map the current route to a navbarDataMap section key, including sub-routes without an exact title match. */
export const resolveSectionKey = (pathname: string, pageTitle: string): string | null => {
  if (navbarDataMap[pageTitle]) {
    return pageTitle;
  }

  if (pathname.includes("/officeholders/")) {
    return "Officeholders";
  }

  if (pathname.includes("/investor-profiles/") || pathname.includes("/switch-holding")) {
    return "Investor profiles";
  }

  if (pathname.includes("/investor-relations/")) {
    return "Investor Relations";
  }

  if (
    pathname.includes("/fundraises") ||
    pathname.includes("/create-fundraise") ||
    pathname.includes("/fundraise-offer")
  ) {
    return "Fundraising";
  }

  if (
    pathname.includes("/investors/all") ||
    pathname.includes("/investors/requests") ||
    pathname.includes("/investors/settings")
  ) {
    return "Investors";
  }

  if (
    pathname.includes("/company-information/notes-") ||
    pathname.includes("/company-information/create-notes") ||
    pathname.includes("/company-information/issue-notes")
  ) {
    return "Notes";
  }

  if (pathname.includes("/company-information/options/")) {
    return "Options";
  }

  if (pathname.includes("/company-information/share-") || pathname.includes("/company-information/share-price")) {
    return "Shares";
  }

  if (
    pathname.includes("/options-register") ||
    pathname.includes("/shares-register") ||
    pathname.includes("/notes-register") ||
    pathname.includes("/expiring-kyc-register") ||
    pathname.includes("/pending-authority-requests")
  ) {
    return "Registers";
  }

  if (pathname.includes("/options/") && pathname.includes("/optionGrantId")) {
    return "Options grant";
  }

  for (const [sectionKey, items] of Object.entries(navbarDataMap)) {
    if (items.some((item) => pathname.includes(`/${item.value}`))) {
      return sectionKey;
    }
  }

  return null;
};

const isOptionsGrantVestPath = (pathname: string): boolean =>
  /\/options\/[^/]+$/.test(pathname) && !pathname.endsWith("/details") && !pathname.endsWith("/exercise");

export const buildSectionTabPath = (
  sectionKey: string,
  value: string,
  companyId: string,
  pathname: string,
  pathnames: string[]
): string => {
  switch (sectionKey) {
    case "Registers":
      return `/company/${companyId}/${value}`;
    case "Fundraising":
      return `/company/${companyId}/${value}`;
    case "Investor Relations":
      return `/company/${companyId}/investor-relations/${value}`;
    case "Investors":
      return `/company/${companyId}/investors/${value}`;
    case "Notes":
      return `/company/${companyId}/company-information/${value}`;
    case "Options":
      return `/company/${companyId}/company-information/options/${value}`;
    case "Officeholders":
      return `/company/${companyId}/company-information/officeholders/${value}`;
    case "Shares":
      return `/company/${companyId}/company-information/${value}`;
    case "Investor profiles":
      return `/investor/company/${companyId}/investor-profiles/${value}`;
    case "Options grant": {
      const base = pathname.replace(/\/(details|vest|exercise)$/, "");
      if (value === "vest" && pathname.includes("/investor/")) {
        return base;
      }
      if (value === "vest") {
        return `${base}/vest`;
      }
      return `${base}/${value}`;
    }
    default: {
      const nextPathnames = [...pathnames];
      nextPathnames.splice(nextPathnames.length - 1, 1);
      return `/${nextPathnames.join("/")}/${value}`;
    }
  }
};

const resolveActiveTab = (pathname: string, navbarItems: NavbarItem[]): string => {
  const vestItem = navbarItems.find((item) => item.value === "vest");
  if (vestItem && isOptionsGrantVestPath(pathname)) {
    return vestItem.value;
  }

  if (navbarItems.some((item) => item.value === "active") && pathname.includes("/switch-holding")) {
    return "active";
  }

  const index = navbarItems.findIndex((data) => pathname.includes(`/${data.value}`));
  return navbarItems[index] ? navbarItems[index].value : navbarItems[0].value;
};

export const usePageSectionTabs = () => {
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);
  const {
    isFundRegistry,
    companyId,
    displayRequestBadge,
    features,
    fundraiseConfig,
    isLegacyPdtiCompany,
    isLoadingCompanyInfo,
    selectedSubscriptionPlan,
    userCompanyRoles,
    isAsicConnectionActivated,
  } = useCompanyContext();

  const [expiringKycCount, setExpiringKycCount] = useState(0);
  const [pendingAuthorityRequestsCount, setPendingAuthorityRequestsCount] = useState(0);
  const [activeTab, setActiveTab] = useState("");

  const pathnames = location.pathname.split("/").filter((x) => x);
  const pageTitle = resolvePageTitle(location.pathname, isAsicConnectionActivated, isFundRegistry);
  const sectionKey = resolveSectionKey(location.pathname, pageTitle);

  const switchTab = useCallback(
    (value: string) => {
      if (!sectionKey || !companyId) {
        return;
      }
      history.push(buildSectionTabPath(sectionKey, value, companyId, location.pathname, pathnames));
    },
    [companyId, history, location.pathname, pathnames, sectionKey]
  );

  const navbarItems = useMemo((): NavbarItem[] | null => {
    if (!sectionKey) {
      return null;
    }

    const removeTab = (nav: NavbarItem[], tabValue: string): NavbarItem[] =>
      nav.filter((data) => data.value !== tabValue);

    if (
      (selectedSubscriptionPlan !== ActivePlan.PLAN3 && sectionKey === "Options") ||
      (currentUser?.isInvestor && sectionKey === "Options grant")
    ) {
      return null;
    }

    if (sectionKey === "Fundraising") {
      if (pageTitle === "Fundraising" && !fundraiseConfig?.fundraiseTncAccepted && features["pdti-full"] === true) {
        return null;
      }
    }

    let navbar: NavbarItem[] | null = navbarDataMap[sectionKey] ? [...navbarDataMap[sectionKey]] : null;

    if (!navbar) {
      return null;
    }

    if (
      ["Investor profiles", "Investors"].includes(sectionKey) &&
      !isLoadingCompanyInfo &&
      !features["add-investors"]
    ) {
      navbar = removeTab(removeTab(navbar, "requests"), "settings");
    }

    if (sectionKey === "Registers") {
      navbar = isFundRegistry
        ? [
            {
              label: "Units",
              value: "shares-register",
            },
          ]
        : [...navbar];

      if (!isLoadingCompanyInfo && userCompanyRoles.isAdmin && !isLegacyPdtiCompany) {
        navbar = [...navbar, EXPIRING_KYC_REGISTER_TAB];
        if (canShowActOnBehalf(features, isFundRegistry)) {
          navbar = [...navbar, PENDING_AUTHORITY_REQUESTS_TAB];
        }
      }
    }

    if (sectionKey === "Shares" && !isLoadingCompanyInfo && features[FeatureIds.TOKEN_CONFIGURATION_ENABLED] !== true) {
      navbar = removeTab(navbar, "share-eligibility");
      navbar = removeTab(navbar, "share-claims");
    }

    if (isApproverReadOnly(userCompanyRoles)) {
      const hiddenTabs = APPROVER_HIDDEN_TABS_BY_SECTION[sectionKey];
      if (hiddenTabs) {
        navbar = hiddenTabs.reduce((acc, tabValue) => removeTab(acc, tabValue), navbar);
      }
    }

    return navbar;
  }, [
    currentUser?.isInvestor,
    features,
    fundraiseConfig?.fundraiseTncAccepted,
    isLegacyPdtiCompany,
    isLoadingCompanyInfo,
    pageTitle,
    sectionKey,
    selectedSubscriptionPlan,
    userCompanyRoles.isAdmin,
    userCompanyRoles.isApprover,
  ]);

  useEffect(() => {
    if (!navbarItems?.length) {
      setActiveTab("");
      return;
    }
    setActiveTab(resolveActiveTab(location.pathname, navbarItems));
  }, [location.pathname, navbarItems]);

  useEffect(() => {
    if (
      !companyId ||
      !userCompanyRoles.isAdmin ||
      isLegacyPdtiCompany ||
      isLoadingCompanyInfo ||
      sectionKey !== "Registers" ||
      !isRegistersPagePath(location.pathname) ||
      typeof companyService.getExpiringKycRegister !== "function"
    ) {
      setExpiringKycCount(0);
      return undefined;
    }

    let cancelled = false;

    companyService
      .getExpiringKycRegister(companyId, "", 0, 1, "kycExpiryDate", "asc")
      .then((response) => {
        if (!cancelled) {
          setExpiringKycCount(response.payload?.count ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExpiringKycCount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, isLegacyPdtiCompany, isLoadingCompanyInfo, location.pathname, sectionKey, userCompanyRoles.isAdmin]);

  useEffect(() => {
    if (
      !companyId ||
      !userCompanyRoles.isAdmin ||
      isLegacyPdtiCompany ||
      isLoadingCompanyInfo ||
      sectionKey !== "Registers" ||
      !isRegistersPagePath(location.pathname) ||
      !canShowActOnBehalf(features, isFundRegistry) ||
      typeof companyService.getActOnBehalfRequests !== "function"
    ) {
      setPendingAuthorityRequestsCount(0);
      return undefined;
    }

    let cancelled = false;

    companyService
      .getActOnBehalfRequests(companyId, 0, 1)
      .then((response) => {
        if (!cancelled) {
          setPendingAuthorityRequestsCount(response.payload?.count ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPendingAuthorityRequestsCount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    companyId,
    features,
    isFundRegistry,
    isLegacyPdtiCompany,
    isLoadingCompanyInfo,
    location.pathname,
    sectionKey,
    userCompanyRoles.isAdmin,
  ]);

  const tabItems = useMemo(() => {
    if (!navbarItems) {
      return [];
    }

    return navbarItems.map((obj) => {
      const showKycCount = obj.value === EXPIRING_KYC_REGISTER_TAB.value && expiringKycCount > 0;
      const showPendingAuthorityCount =
        obj.value === PENDING_AUTHORITY_REQUESTS_TAB.value && pendingAuthorityRequestsCount > 0;
      const showRequestDot = obj.value === "requests" && displayRequestBadge;
      return {
        id: obj.value,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, position: "relative" }}>
            {obj.label}
            {showKycCount ? (
              <Badge tone="negative" size="sm">
                {formatTabCount(expiringKycCount)}
              </Badge>
            ) : null}
            {showPendingAuthorityCount ? (
              <Badge tone="negative" size="sm">
                {formatTabCount(pendingAuthorityRequestsCount)}
              </Badge>
            ) : null}
            {showRequestDot ? (
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--red)", flex: "none" }} />
            ) : null}
          </span>
        ),
      };
    });
  }, [navbarItems, displayRequestBadge, expiringKycCount, pendingAuthorityRequestsCount]);

  return {
    hasTabs: tabItems.length > 0,
    tabItems,
    activeTab,
    switchTab,
    sectionKey,
  };
};
